import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CheckCircle, Lock, Phone } from "lucide-react";
import { canRequest, requestPayload, reunionToday } from "./reservationForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass = "w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:outline-none focus:border-[#527767] focus:ring-1 focus:ring-[#527767]";
const money = value => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(value);

export default function ReservationPage() {
  const { vehicleId } = useParams();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleLoading, setVehicleLoading] = useState(true);
  const [vehicleError, setVehicleError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const sending = useRef(false);
  const [form, setForm] = useState({
    vehicle_id: vehicleId || "", date_mariage: "", client_name: "",
    client_email: "", client_telephone: "", client_ville: "", notes: "",
    lieu_ceremonie: "", salle_reception: "", theme_decoration: "", champagne: "",
    consentement: false,
  });
  const set = (key, value) => setForm(previous => ({ ...previous, [key]: value }));
  const today = reunionToday();

  useEffect(() => {
    let cancelled = false;
    setVehicleLoading(true);
    setVehicleError(false);
    axios.get(`${API}/vehicles`).then(({ data }) => {
      if (cancelled) return;
      const available = data.filter(vehicle => vehicle.is_available !== false);
      setVehicles(available);
      setForm(previous => ({ ...previous, vehicle_id:
        available.some(vehicle => vehicle.id === previous.vehicle_id) ? previous.vehicle_id :
          available.length === 1 ? available[0].id : "" }));
    }).catch(() => { if (!cancelled) setVehicleError(true); })
      .finally(() => { if (!cancelled) setVehicleLoading(false); });
    return () => { cancelled = true; };
  }, [retry]);

  useEffect(() => {
    let cancelled = false;
    const date = form.date_mariage;
    setAvailability(null);
    if (!date || date < today) return () => { cancelled = true; };
    axios.get(`${API}/availability`, { params: { date_mariage: date } })
      .then(({ data }) => { if (!cancelled) setAvailability({ date, available: data.available === true }); })
      .catch(err => {
        if (!cancelled) setAvailability({ date, available: false, error: err.response?.status !== 409 });
      });
    return () => { cancelled = true; };
  }, [form.date_mariage, today, retry]);

  const vehicle = vehicles.find(item => item.id === form.vehicle_id);
  const ready = canRequest(form, vehicle, availability, today);
  const deposit = Math.min(200, vehicle?.price || 0);

  const submit = async event => {
    event.preventDefault();
    if (!ready || sending.current) return;
    sending.current = true;
    setLoading(true);
    setError("");
    try {
      // The server rechecks availability before creating the quote.
      const response = await axios.post(`${API}/devis`, requestPayload(form));
      setCreated(response.data);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail :
        "L’envoi n’a pas abouti. Réessayez ou appelez-nous au 06 93 39 74 64.");
      if (err.response?.status === 409) setAvailability({ date: form.date_mariage, available: false });
    } finally {
      sending.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="font-sans min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 bg-[#f8f7f3] px-4 py-8 sm:py-12">
        <div className="max-w-xl mx-auto">
          {created ? (
            <section className="rounded-2xl bg-white p-5 sm:p-8 shadow-sm" data-testid="request-success">
              <CheckCircle className="text-[#527767] mb-4" size={36} />
              <h1 className="text-2xl font-bold text-gray-900">Votre demande est enregistrée</h1>
              <p className="mt-3 text-gray-600">Référence {created.numero} · Mariage du {form.date_mariage.split("-").reverse().join("/")}</p>
              <p className="mt-2 text-sm text-gray-600">
                {created.email_sent && !created.email_simulated ? `Votre devis a été envoyé à ${form.client_email}.` :
                  "L’envoi du devis par e-mail n’est pas confirmé. Votre demande est bien conservée ; contactez-nous si nécessaire."}
              </p>
              <div className="my-6 rounded-xl bg-[#f1f6f3] p-4 space-y-2 text-sm">
                <p className="flex justify-between"><span>Total de la prestation</span><strong>{money(created.montant ?? vehicle.price)}</strong></p>
                <p className="flex justify-between"><span>Acompte</span><strong>{money(deposit)}</strong></p>
                <p className="flex justify-between"><span>Solde après acompte</span><strong>{money((created.montant ?? vehicle.price) - deposit)}</strong></p>
              </div>
              <h2 className="text-lg font-semibold">Vous souhaitez réserver ?</h2>
              <p className="mt-2 mb-4 text-sm text-gray-600">La date sera réservée après réception de l’acompte et validation du paiement. Les détails du mariage seront précisés ensemble ensuite.</p>
              <a data-testid="pay-acompte-btn" href={`/paiement?devis=${encodeURIComponent(created.id)}&type=acompte`}
                className="flex items-center justify-center gap-2 rounded-lg bg-[#527767] px-4 py-3 font-semibold text-white">
                <Lock size={16} /> Réserver avec {money(deposit)} d’acompte
              </a>
              <p className="mt-3 text-center text-xs text-gray-500">Paiement sécurisé par Stripe. Aucun paiement nécessaire pour une simple demande.</p>
              <a href="/" className="block mt-5 text-center text-sm underline text-gray-600">Je souhaite seulement le devis pour le moment</a>
            </section>
          ) : (
            <>
              <header className="mb-6">
                <p className="text-xs uppercase tracking-widest font-semibold text-[#527767] mb-2">RM Location Prestige</p>
                <h1 className="text-3xl font-bold text-gray-900">Votre mariage, simplement</h1>
                <p className="mt-2 text-gray-600">Un devis ou une réservation : vos coordonnées et la date suffisent. Nous verrons les détails ensemble.</p>
              </header>
              <form onSubmit={submit} className="rounded-2xl bg-white p-5 sm:p-7 shadow-sm" data-testid="simple-reservation-form">
                {vehicleLoading ? <p role="status" className="mb-5 text-sm">Chargement de la prestation…</p> :
                  vehicleError ? <div role="alert" className="mb-5 text-sm text-red-700">La prestation ne peut pas être chargée. <button type="button" className="underline" onClick={() => setRetry(n => n + 1)}>Réessayer</button></div> :
                  !vehicles.length ? <p role="alert" className="mb-5 text-sm">Aucun véhicule disponible en ligne. Appelez-nous au 06 93 39 74 64.</p> :
                  vehicles.length > 1 ? <label className="block mb-5 text-sm font-medium">Véhicule *
                    <select required className={`${inputClass} mt-1`} value={form.vehicle_id} onChange={e => set("vehicle_id", e.target.value)}>
                      <option value="">Choisir votre véhicule</option>
                      {vehicles.map(item => <option key={item.id} value={item.id}>{item.name} - {money(item.price)}</option>)}
                    </select>
                  </label> : null}
                {vehicle && <div className="flex items-center gap-3 rounded-xl bg-[#f1f6f3] p-3 mb-5">
                  {vehicle.images?.[0] && <img src={vehicle.images[0]} alt={vehicle.name} className="h-16 w-20 rounded-lg object-cover" />}
                  <div><p className="font-semibold text-gray-900">{vehicle.name} · {money(vehicle.price)}</p>
                    <p className="text-xs text-gray-600 mt-1">Décoration personnalisée et champagne offert</p></div>
                </div>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label htmlFor="wedding-date" className="block text-sm font-medium sm:col-span-2">Date du mariage *
                    <input id="wedding-date" data-testid="date-mariage-input" type="date" required min={today} value={form.date_mariage}
                      onChange={e => set("date_mariage", e.target.value)} className={`${inputClass} mt-1`} />
                  </label>
                  {form.date_mariage && <div role="status" aria-live="polite" className="sm:col-span-2 text-sm -mt-2">
                    {form.date_mariage < today ? <span className="text-red-700">Choisissez une date à venir.</span> :
                      availability?.date !== form.date_mariage ? <span className="text-gray-500">Vérification de la disponibilité…</span> :
                      availability.error ? <span className="text-amber-800">Vérification indisponible. <button type="button" onClick={() => setRetry(n => n + 1)} className="underline">Réessayer</button></span> :
                      availability.available ? <span className="text-[#527767]">Date disponible à ce stade. Elle sera bloquée après paiement de l’acompte.</span> :
                        <span className="text-red-700">Cette date est déjà réservée. Choisissez une autre date ou contactez-nous.</span>}
                  </div>}
                  <label htmlFor="client-name" className="block text-sm font-medium sm:col-span-2">Prénom et nom *
                    <input id="client-name" autoComplete="name" required maxLength={150} value={form.client_name} onChange={e => set("client_name", e.target.value)} className={`${inputClass} mt-1`} />
                  </label>
                  <label htmlFor="client-phone" className="block text-sm font-medium">Téléphone *
                    <input id="client-phone" type="tel" autoComplete="tel" required maxLength={30} value={form.client_telephone} onChange={e => set("client_telephone", e.target.value)} className={`${inputClass} mt-1`} placeholder="06 92…" />
                  </label>
                  <label htmlFor="client-email" className="block text-sm font-medium">E-mail *
                    <input id="client-email" type="email" autoComplete="email" required maxLength={254} value={form.client_email} onChange={e => set("client_email", e.target.value)} className={`${inputClass} mt-1`} />
                  </label>
                </div>
                <details className="mt-5 border-t border-gray-100 pt-4">
                  <summary className="cursor-pointer text-sm font-medium text-[#527767]">Une précision à ajouter ? (facultatif)</summary>
                  <div className="mt-4 space-y-4">
                    <label htmlFor="client-city" className="block text-sm">Votre commune
                      <input id="client-city" autoComplete="address-level2" value={form.client_ville} onChange={e => set("client_ville", e.target.value)} className={`${inputClass} mt-1`} />
                    </label>
                    {[['lieu_ceremonie', 'Lieu de cérémonie'], ['salle_reception', 'Lieu de réception'], ['theme_decoration', 'Thème ou couleurs']].map(([key, label]) =>
                      <label key={key} htmlFor={key} className="block text-sm">{label}
                        <input id={key} value={form[key]} onChange={e => set(key, e.target.value)} className={`${inputClass} mt-1`} />
                      </label>)}
                    <label htmlFor="champagne" className="block text-sm">Champagne offert
                      <select id="champagne" value={form.champagne} onChange={e => set("champagne", e.target.value)} className={`${inputClass} mt-1 bg-white`}>
                        <option value="">À préciser plus tard</option><option>Avec alcool</option><option>Sans alcool</option><option>Non</option>
                      </select>
                    </label>
                    <label htmlFor="request-notes" className="block text-sm">Votre message
                      <textarea id="request-notes" rows={3} maxLength={3000} value={form.notes} onChange={e => set("notes", e.target.value)} className={`${inputClass} mt-1`} placeholder="Une question, un rendez-vous pour voir la voiture, vos horaires…" />
                    </label>
                  </div>
                </details>
                <label className="mt-5 flex items-start gap-3 text-sm text-gray-600">
                  <input type="checkbox" required checked={form.consentement} onChange={e => set("consentement", e.target.checked)} className="mt-1 accent-[#527767]" />
                  <span>J’accepte d’être contacté(e) au sujet de ma demande. *</span>
                </label>
                {error && <p role="alert" className="mt-4 text-sm text-red-700">{error}</p>}
                <button type="submit" data-testid="submit-devis-btn" disabled={!ready || loading}
                  className="mt-5 w-full rounded-lg bg-[#527767] px-4 py-3 font-semibold text-white hover:bg-[#416052] disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? "Envoi en cours…" : "Recevoir mon devis gratuit"}
                </button>
                <p className="mt-3 text-center text-xs text-gray-500">Sans engagement. Vous pourrez ensuite réserver avec l’acompte de 200 €.</p>
              </form>
              <a href="tel:0693397464" className="mt-5 flex items-center justify-center gap-2 text-sm text-gray-600"><Phone size={15} /> Une question ? 06 93 39 74 64</a>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
